import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectAddedAddressesIds } from '../../../../store/selectors/addressBook';
import { NotificationListEnum } from '../../../../types/shell';
import { getCountLabel } from '../../../../utils/helpers';
import notification from '../../../../utils/notification';

export const useAddressUploadSuccess = () => {
  const addedAddressesIds = useSelector(selectAddedAddressesIds);
  const [totalAddedCount, setTotalAddedCount] = useState(0);
  const prevCountRef = useRef(0);

  const addedAddressesCount = useMemo(
    () => (addedAddressesIds ? Object.keys(addedAddressesIds).length : 0),
    [addedAddressesIds],
  );

  useEffect(() => {
    if (addedAddressesCount > prevCountRef.current) {
      const diff = addedAddressesCount - prevCountRef.current;
      setTotalAddedCount((prev) => prev + diff);
    }

    prevCountRef.current = addedAddressesCount;
  }, [addedAddressesCount]);

  const addedAddressesLabel = useMemo(
    () => getCountLabel(totalAddedCount, 'address was', 'addresses were'),
    [totalAddedCount],
  );

  const resetCount = useCallback(() => {
    setTotalAddedCount(0);
    prevCountRef.current = 0;
  }, []);

  const handleCloseWithSuccess = useCallback(
    (onClose: () => void) => {
      onClose();
      notification.success(NotificationListEnum.Success, {
        content: `${addedAddressesLabel} added successfully`,
      });
      resetCount();
    },
    [addedAddressesLabel, resetCount],
  );

  return {
    addedAddressesIds,
    addedAddressesCount: totalAddedCount,
    addedAddressesLabel,
    handleCloseWithSuccess,
    resetCount,
  };
};
